import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const config = JSON.parse(document.getElementById('nl-mini-gift-config')?.textContent || '{}');
const root = window.Shopify?.routes?.root || '/';
const fullSizeIds = new Set((config.qualifyingProducts || []).map(String));
const giftVariant = Number(config.variantId);
let pending = Promise.resolve();
let checkoutReady = false;

const sectionIds = () => [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), element => element.dataset.sectionId))].slice(0, 5);
const readCart = async () => {
  const response = await fetch(root + 'cart.js', {headers: {Accept: 'application/json'}, credentials: 'same-origin'});
  if (!response.ok) throw new Error('Cart could not be read');
  return response.json();
};
const mutate = async (endpoint, payload) => {
  const response = await fetch(root + endpoint, {
    method: 'POST',
    headers: {'Content-Type': 'application/json', Accept: 'application/json'},
    credentials: 'same-origin',
    body: JSON.stringify({...payload, sections: sectionIds(), sections_url: window.location.pathname}),
  });
  const result = await response.json();
  if (!response.ok || result.status || result.errors) throw new Error(result.description || result.message || 'Gift could not be updated');
  return result;
};
const isGift = item => Number(item.variant_id) === giftVariant && item.properties?._nl_complimentary === 'fall';

async function reconcile() {
  let cart = await readCart();
  let changed = false;
  let sections;
  const gifts = cart.items.filter(isGift);
  const qualifies = cart.items.some(item => fullSizeIds.has(String(item.product_id)) && item.quantity > 0);
  const purchasedMini = cart.items.some(item => Number(item.variant_id) === giftVariant && !isGift(item));
  const updates = {};
  if (!qualifies || purchasedMini) {
    gifts.forEach(item => { updates[item.key] = 0; });
  } else {
    gifts.forEach((item, index) => { if (index > 0 || item.quantity !== 1) updates[item.key] = index === 0 ? 1 : 0; });
  }
  if (Object.keys(updates).length) {
    const result = await mutate('cart/update.js', {updates});
    sections = result.sections;
    cart = result;
    changed = true;
  }
  if (qualifies && !purchasedMini && !cart.items.some(isGift) && config.available) {
    try {
      const result = await mutate('cart/add.js', {items: [{id: giftVariant, quantity: 1, properties: {_nl_complimentary: 'fall'}}]});
      sections = result.sections;
      cart = await readCart();
      changed = true;
    } catch (error) {
      console.warn('[Nouralume gift]', error.message);
    }
  }
  const chargeableGifts = cart.items.filter(item => isGift(item) && item.final_line_price !== 0);
  if (chargeableGifts.length) {
    const result = await mutate('cart/update.js', {updates: Object.fromEntries(chargeableGifts.map(item => [item.key, 0]))});
    sections = result.sections;
    cart = result;
    changed = true;
  }
  return {...cart, sections, giftChanged: changed};
}

export function ensureMiniGift() {
  if (!giftVariant || !fullSizeIds.size) return readCart();
  const next = pending.catch(() => {}).then(reconcile);
  pending = next;
  return next;
}

function announce(cart) {
  if (!cart.giftChanged) return;
  document.dispatchEvent(new CartLinesUpdateEvent({
    action: 'update',
    context: 'cart',
    lines: [],
    promise: Promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {items: cart.items, sections: cart.sections, source: 'nouralume-mini-gift'},
    }),
  }));
}

document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  event.promise?.then(({detail}) => {
    if (detail?.source === 'nouralume-mini-gift' || detail?.source === 'product-form-component' || detail?.source === 'cart-items-component' || detail?.didError) return;
    ensureMiniGift().then(announce).catch(error => console.warn('[Nouralume gift]', error.message));
  }).catch(() => {});
});

document.addEventListener('submit', event => {
  if (checkoutReady || !(event.target instanceof HTMLFormElement) || event.submitter?.name !== 'checkout') return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const form = event.target;
  const submitter = event.submitter;
  ensureMiniGift().then(cart => {
    announce(cart);
    checkoutReady = true;
    const currentForm = document.querySelector('form[action*="/cart"]:has([name="checkout"])') || form;
    const currentSubmitter = currentForm.querySelector('[name="checkout"]');
    currentForm.requestSubmit(currentSubmitter || (submitter.isConnected ? submitter : undefined));
  }).catch(error => console.warn('[Nouralume gift]', error.message));
}, true);

ensureMiniGift().then(announce).catch(error => console.warn('[Nouralume gift]', error.message));
window.addEventListener('pageshow', event => {
  if (event.persisted) ensureMiniGift().then(announce).catch(error => console.warn('[Nouralume gift]', error.message));
});
